// frontend/src/components/client/ClientProfile.jsx
import React, { useState, useEffect } from 'react';
import { User, Mail, Phone, Building, MapPin, Plane, Bell, MessageCircle, Save, CheckCircle } from 'lucide-react';
import { getProfile, getMyAircraft, getNotificationPreferences, updateNotificationPreferences } from '../../services/clientApi';

const InfoRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3 p-3 bg-gray-900/50 rounded-xl mb-2">
    <Icon className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
    <div className="flex-1 min-w-0">
      <p className="text-xs text-gray-500">{label}</p>
      <p className="text-white break-words mt-0.5">{value || 'Not specified'}</p>
    </div>
  </div>
);

const SectionTitle = ({ icon: Icon, title }) => (
  <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
    <Icon className="w-4 h-4 text-blue-400" />
    {title}
  </h3>
);

const ClientProfile = () => {
  const [profile, setProfile] = useState(null);
  const [aircraft, setAircraft] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [smsNotifications, setSmsNotifications] = useState(false);
  const [telegramNotifications, setTelegramNotifications] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [profileRes, aircraftRes, notifRes] = await Promise.all([
        getProfile(), getMyAircraft(), getNotificationPreferences()
      ]);
      setProfile(profileRes.data);
      setAircraft(aircraftRes.data);
      if (notifRes.data) {
        setEmailNotifications(notifRes.data.email_notifications !== false);
        setSmsNotifications(notifRes.data.sms_notifications === true);
        setTelegramNotifications(notifRes.data.telegram_notifications === true);
      }
    } catch (error) {
      console.error('Error loading profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const savePreferences = async () => {
    setSaving(true); setSaveSuccess(false);
    try {
      await updateNotificationPreferences({ email_notifications: emailNotifications, sms_notifications: smsNotifications, telegram_notifications: telegramNotifications });
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      alert('Failed to save notification preferences');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
      </div>
    );
  }

  const NotifToggle = ({ icon: Icon, iconColor, title, subtitle, checked, onChange, disabled }) => (
    <label className={`flex items-start justify-between p-3 bg-gray-900/50 rounded-xl cursor-pointer hover:bg-gray-900/70 active:bg-gray-900/90 transition ${disabled ? 'opacity-50' : ''}`}>
      <div className="flex items-start gap-3 flex-1 min-w-0 mr-3">
        <Icon className={`w-5 h-5 ${iconColor} flex-shrink-0 mt-0.5`} />
        <div className="min-w-0">
          <span className="text-white text-sm font-medium">{title}</span>
          <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{subtitle}</p>
        </div>
      </div>
      {/* Custom toggle switch */}
      <div className="relative flex-shrink-0 mt-0.5">
        <input type="checkbox" className="sr-only" checked={checked} onChange={onChange} disabled={disabled} />
        <div className={`w-11 h-6 rounded-full transition-colors ${checked ? 'bg-blue-500' : 'bg-gray-600'}`}>
          <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${checked ? 'translate-x-5' : ''}`} />
        </div>
      </div>
    </label>
  );

  return (
    <div className="px-4 sm:px-0 max-w-2xl mx-auto pb-8">
      <div className="mb-5">
        <h2 className="text-xl font-bold text-white">My Profile</h2>
        <p className="text-gray-400 text-sm">Your information and notification settings</p>
      </div>

      {/* Company Information */}
      <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-4 mb-4">
        <SectionTitle icon={Building} title="Company Information" />
        <InfoRow icon={Building} label="Company" value={profile?.company_name} />
        <InfoRow icon={Mail} label="Email" value={profile?.email} />
        <InfoRow icon={User} label="Full Name" value={`${profile?.first_name || ''} ${profile?.last_name || ''}`.trim()} />
      </div>

      {/* Contact Information */}
      <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-4 mb-4">
        <SectionTitle icon={Phone} title="Contact Information" />
        <InfoRow icon={Phone} label="Phone" value={profile?.phone} />
        <InfoRow icon={MapPin} label="Address" value={profile?.address} />
        {profile?.city && (
          <InfoRow icon={MapPin} label="City / State / Zip" value={`${profile?.city || ''} ${profile?.state || ''} ${profile?.zip_code || ''}`.trim()} />
        )}
      </div>

      {/* Notification Preferences */}
      <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-4 mb-4">
        <SectionTitle icon={Bell} title="Notification Preferences" />
        <p className="text-gray-400 text-sm mb-4">
          Choose how you want to receive notifications about your requests
        </p>
        <div className="space-y-2">
          <NotifToggle
            icon={Mail} iconColor="text-blue-400" title="Email Notifications"
            subtitle={`Updates via email at ${profile?.email || 'your email'}`}
            checked={emailNotifications} onChange={(e) => setEmailNotifications(e.target.checked)}
          />
          <NotifToggle
            icon={Phone} iconColor="text-green-400" title="SMS Notifications"
            subtitle={profile?.phone ? `Text message at ${profile.phone}` : 'No phone number on file'}
            checked={smsNotifications} onChange={(e) => setSmsNotifications(e.target.checked)}
            disabled={!profile?.phone}
          />
          <NotifToggle
            icon={MessageCircle} iconColor="text-sky-400" title="Telegram Notifications"
            subtitle={profile?.telegram_username ? `@${profile.telegram_username}` : 'No Telegram username on file'}
            checked={telegramNotifications} onChange={(e) => setTelegramNotifications(e.target.checked)}
            disabled={!profile?.telegram_username}
          />
        </div>
        <div className="mt-4 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
          {saveSuccess && (
            <span className="flex items-center justify-center gap-1 text-green-400 text-sm">
              <CheckCircle className="w-4 h-4" />
              Preferences saved!
            </span>
          )}
          <button
            onClick={savePreferences}
            disabled={saving}
            className="sm:ml-auto w-full sm:w-auto px-5 py-3 sm:py-2 bg-blue-500 hover:bg-blue-600 active:bg-blue-700 rounded-xl text-white text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50 transition"
          >
            {saving ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            {saving ? 'Saving...' : 'Save Preferences'}
          </button>
        </div>
      </div>

      {/* Aircraft Section */}
      <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-4 mb-4">
        <SectionTitle icon={Plane} title={`My Aircraft (${aircraft.length})`} />
        {aircraft.length === 0 ? (
          <p className="text-gray-500 text-sm">No aircraft registered</p>
        ) : (
          <div className="space-y-2">
            {aircraft.map((a) => (
              <div key={a.id} className="flex items-center justify-between p-3 bg-gray-900/50 rounded-xl">
                <div className="min-w-0">
                  <p className="text-white font-medium">{a.tail_number}</p>
                  <p className="text-xs text-gray-500 truncate">{a.manufacturer} {a.model} ({a.year})</p>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ml-2 ${a.status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}`}>
                  {a.status || 'Active'}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="text-center">
        <p className="text-xs text-gray-500">
          For changes to your profile, please contact your FBO administrator.
        </p>
      </div>
    </div>
  );
};

export default ClientProfile;